import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { TrendingUp, MapPin, DollarSign, Target, Award, CheckCircle, Clock, BarChart3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import api from '../../services/api';

const MyPerformancePage = () => {
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchPerformance();
    }
  }, [activeBusiness?.id]);

  const fetchPerformance = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/part5/intelligence/me/today');
      setPerformance(data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error('Failed to load performance data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const completionRate = performance?.total_visits_planned > 0
    ? Math.round((performance.total_visits_completed / performance.total_visits_planned) * 100)
    : 0;

  const getScoreColor = (score) => {
    if (!score) return 'bg-slate-100 text-slate-600';
    if (score >= 80) return 'bg-emerald-50 text-emerald-700';
    if (score >= 60) return 'bg-amber-50 text-amber-700';
    return 'bg-rose-50 text-rose-700';
  };

  const getScoreLabel = (score) => {
    if (!score) return 'No Data';
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Improvement';
  };

  return (
    <div className="h-full flex flex-col" data-testid="my-performance-page">
      {/* Premium Header */}
      <ListPageHeader
        title="My Performance"
        subtitle="Today&apos;s activity and performance metrics"
        stats={[
          { icon: Target, label: 'Visits', value: `${performance?.total_visits_completed || 0}/${performance?.total_visits_planned || 0}`, color: 'blue' },
          { icon: MapPin, label: 'Distance', value: `${performance?.total_distance_km?.toFixed(1) || 0} km`, color: 'teal' },
          { icon: DollarSign, label: 'Collections', value: formatCompactCurrency(performance?.total_collections_amount), color: 'emerald' },
          { icon: Award, label: 'Score', value: `${performance?.score_overall?.toFixed(0) || 0}/100`, color: performance?.score_overall >= 80 ? 'emerald' : performance?.score_overall >= 60 ? 'amber' : 'rose' },
        ]}
      />

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading performance data...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Overall Status Card */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-teal-50/50 to-transparent flex items-center justify-between">
                <div>
                  <h3 className="text-[14px] font-semibold text-slate-700">Overall Performance</h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">Your score for today</p>
                </div>
                <Award className="w-10 h-10 text-teal-200" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[40px] font-bold text-slate-800">{performance?.score_overall?.toFixed(0) || 0}</p>
                    <p className="text-[12px] text-slate-500">out of 100</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium ${getScoreColor(performance?.score_overall)}`}>
                    {getScoreLabel(performance?.score_overall)}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all ${
                      performance?.score_overall >= 80 ? 'bg-emerald-500' :
                      performance?.score_overall >= 60 ? 'bg-amber-500' :
                      'bg-rose-500'
                    }`}
                    style={{ width: `${performance?.score_overall || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[14px] font-semibold text-slate-700">Quick Stats</h3>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-blue-500" />
                    <span className="text-[11px] text-slate-500 uppercase tracking-wider">Completion</span>
                  </div>
                  <p className="text-[22px] font-semibold text-slate-800">{completionRate}%</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <span className="text-[11px] text-slate-500 uppercase tracking-wider">Productivity</span>
                  </div>
                  <p className="text-[22px] font-semibold text-slate-800">{performance?.score_productivity?.toFixed(0) || 0}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    <span className="text-[11px] text-slate-500 uppercase tracking-wider">Collection</span>
                  </div>
                  <p className="text-[22px] font-semibold text-slate-800">{performance?.score_collection?.toFixed(0) || 0}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-teal-500" />
                    <span className="text-[11px] text-slate-500 uppercase tracking-wider">Time/Visit</span>
                  </div>
                  <p className="text-[22px] font-semibold text-slate-800">{performance?.avg_time_per_visit || 0}m</p>
                </div>
              </div>
            </div>

            {/* Performance Breakdown */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden lg:col-span-2">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[14px] font-semibold text-slate-700">Performance Breakdown</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Detailed score analysis</p>
              </div>
              <div className="p-5 space-y-5">
                {/* Productivity */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[13px] font-medium text-slate-700">Productivity Score</span>
                    <span className="text-[13px] font-semibold text-slate-800">
                      {performance?.score_productivity?.toFixed(0) || 0}/100
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${performance?.score_productivity || 0}%` }}
                    />
                  </div>
                </div>

                {/* Collection */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[13px] font-medium text-slate-700">Collection Performance</span>
                    <span className="text-[13px] font-semibold text-slate-800">
                      {performance?.score_collection?.toFixed(0) || 0}/100
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: `${performance?.score_collection || 0}%` }}
                    />
                  </div>
                </div>

                {/* Overall */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[13px] font-medium text-slate-700">Overall Score</span>
                    <span className="text-[13px] font-semibold text-slate-800">
                      {performance?.score_overall?.toFixed(0) || 0}/100
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        performance?.score_overall >= 80 ? 'bg-emerald-500' :
                        performance?.score_overall >= 60 ? 'bg-amber-500' :
                        'bg-rose-500'
                      }`}
                      style={{ width: `${performance?.score_overall || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tips Card */}
            <div className="bg-teal-50 rounded-md border border-teal-200 overflow-hidden lg:col-span-2">
              <div className="p-4">
                <h3 className="text-[14px] font-semibold text-teal-800 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Tips to Improve
                </h3>
                <ul className="space-y-2 text-[12px] text-teal-700">
                  {completionRate < 80 && (
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      Try to complete all assigned visits for better productivity score
                    </li>
                  )}
                  {(performance?.total_collections_amount || 0) < 5000 && (
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      Focus on collections to improve your collection performance score
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Keep your manager updated on any challenges during visits
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Use navigation to optimize your route and save time
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPerformancePage;
